import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a size chart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends DialogComponent
 */
export class SizeChartDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    
    // Add event listener for the size chart button
    document.addEventListener('click', this.#handleSizeChartButtonClick);
    
    // Handle image loading
    this.#setupImageLoading();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleSizeChartButtonClick);
  }

  /**
   * Setup image loading event handlers
   */
  #setupImageLoading() {
    const image = /** @type {HTMLImageElement} */ (this.querySelector('#size-chart-image'));
    const loader = /** @type {HTMLElement} */ (this.querySelector('#size-chart-loader'));
    
    if (image && loader) {
      // Handle successful image load
      image.addEventListener('load', () => {
        loader.style.display = 'none';
        image.style.display = 'block';
      });
      
      // Handle image load error
      image.addEventListener('error', () => {
        loader.style.display = 'none';
        image.style.display = 'block';
      });
      
      // If image is already loaded (cached), hide loader immediately
      if (image.complete && image.naturalHeight !== 0) {
        loader.style.display = 'none';
        image.style.display = 'block';
      }
    }
  }

  /**
   * Handle size chart button click events
   * @param {Event} event - The click event
   */
  #handleSizeChartButtonClick = (event) => {
    if (!event.target) return;
    
    const sizeChartButton = /** @type {Element} */ (event.target).closest('[data-size-chart-trigger]');
    if (!sizeChartButton) return;

    event.preventDefault();
    this.showDialog();
    
    // Setup image loading after dialog opens
    setTimeout(() => {
      this.#setupImageLoading();
    }, 100);
  };
}

if (!customElements.get('size-chart-drawer-component')) {
  customElements.define('size-chart-drawer-component', SizeChartDrawerComponent);
}